import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Q } from '@nozbe/watermelondb'
import { ArrowAllCalendars, StorageLimitIcon } from 'assets/icons'
import {
  DisplayModeIcon,
  MenuFirstDayIcon,
  MenuLanguageIcon,
  MenuLinkedCalendarsIcon
} from 'assets/icons/menu'
import { PlanName } from 'components/dialogs/payment/PlansInfoDialog'
import StorageProgressLoader from 'components/loaders/StorageProgressLoader'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import {
  APP_THEME_MODES,
  AppThemeMode,
  useAppSettings
} from 'components/providers/AppSettingsProvider'
import { usePayment } from 'components/providers/PaymentProvider'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAccountStorage } from 'services/api/account'
import { updateUserSettings } from 'services/api/userSettings'
import Database from 'services/db/Database'
import { AccountDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { isGroupcalApp } from 'utils/appType'

import PickLanguagePopup, { langs } from '../PickLanguagePopup'
import SelectItemWithSearchPopup from '../SelectItemWithSearchPopup'
import DefaultCalendarSection from './DefaultCalendarSection'
import FallbackTimezoneSetting from './FallbackTimezoneSetting'
import StorageByGroupPopup, { StorageByGroupItem } from './StorageByGroupPopup'
import { days } from './UserMenuPopup'

export interface SettingsSectionProps {
  account?: Model<AccountDoc>
  userSettings: Model<UserSettingsDoc> | undefined
  setMenuOpen: (b: boolean) => void
  onOpenLinkedCalendars?: () => void
}

function nextRecommendedPlan(currentTier: Tier): PlanName | null {
  switch (currentTier) {
    case Tier.FREE:
      return 'tier1'
    case Tier.PRO:
      return 'tier2'
    case Tier.BUSINESS:
      return 'tier3'
    case Tier.BUSINESS_PLUS:
      return 'tier4'
    default:
      return null
  }
}

export default function SettingsSection(props: SettingsSectionProps) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const appSettings = useAppSettings()
  const { showPaymentDialog } = usePayment()

  const [showLanguagePopup, onShowLanguagePopup] = useState(false)
  const [showFirstDayPopup, onShowFirstDayPopup] = useState(false)
  const [showThemePopup, onShowThemePopup] = useState(false)
  const [showStoragePopup, setShowStoragePopup] = useState(false)
  const [isStorageLoading, setIsStorageLoading] = useState(true)
  const [totalPhotos, setTotalPhotos] = useState(0)
  const [photoQuotaLimit, setPhotoQuotaLimit] = useState<number | null>(0)
  const [photosByGroup, setPhotosByGroup] = useState<Record<string, number>>({})
  const [groupById, setGroupById] = useState<Record<string, Model<GroupDoc>>>(
    {}
  )

  const accentColor = theme.palette.primary.main
  const storageProgressColor =
    theme.palette.mode === 'dark' ? '#EBEBF599' : '#3C3C4399'

  const usageRatio = useMemo(() => {
    if (photoQuotaLimit == null || photoQuotaLimit <= 0) return 0
    return totalPhotos / photoQuotaLimit
  }, [photoQuotaLimit, totalPhotos])

  const usagePercent = Math.max(0, Math.min(100, usageRatio * 100))
  const isNearQuota =
    photoQuotaLimit != null && photoQuotaLimit > 0 && usageRatio >= 0.9
  const isLtr = i18n.dir() === 'ltr'
  // Row anchors (theme.spacing = 0.25rem * factor, i.e. spacing(3) = 12px, spacing(1.5) = 6px):
  // icon span width (20px) + icon margin (spacing(3) = 12px) + inner action padding (spacing(1.5) = 6px) = 38px.
  // Engulf padding on the value side is 8px.
  const storageBarStart = '38px'
  const storageBarEnd = '16px'

  const storageValue = `${totalPhotos}/${
    photoQuotaLimit == null || photoQuotaLimit < 0
      ? t('unlimited')
      : photoQuotaLimit
  }`
  const hasPhotosUsed = totalPhotos > 0 && isGroupcalApp()
  const storageValueMinGap = isLtr ? '12px' : hasPhotosUsed ? 0 : '16px'
  const storageValueMinGapRtl = !isLtr ? '12px' : hasPhotosUsed ? 0 : '16px'

  const activeTier = highestActiveTier(props.account)
  const recommendedPlan = nextRecommendedPlan(activeTier)

  const showLangauge = () => {
    onShowLanguagePopup(true)
  }

  const onLangChanged = (lang: string) => {
    window.location.reload()
    i18n.changeLanguage(lang)
  }

  const showFirstDay = () => {
    onShowFirstDayPopup(true)
  }

  const showThemeSelector = () => {
    onShowThemePopup(true)
  }

  const showStorageByGroup = () => {
    setShowStoragePopup(true)
  }

  const openLinkedCalendars = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    props.setMenuOpen(false)
    props.onOpenLinkedCalendars?.()
  }

  useEffect(() => {
    const accountId = props.account?._id
    if (!accountId) return

    let isMounted = true
    const abortController = new AbortController()

    getAccountStorage(accountId, abortController.signal)
      .then((storage) => {
        if (!isMounted) return
        setTotalPhotos(storage.totalPhotos ?? 0)
        setPhotoQuotaLimit(
          storage.photoQuotaLimit === undefined ? 0 : storage.photoQuotaLimit
        )
        setPhotosByGroup(storage.photosByGroup ?? {})
        setIsStorageLoading(false)
      })
      .catch(() => {
        if (!isMounted) return
        setTotalPhotos(0)
        setPhotoQuotaLimit(0)
        setPhotosByGroup({})
        setIsStorageLoading(false)
      })

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [props.account?._id])

  useEffect(() => {
    const groupIds = Object.keys(photosByGroup)
    if (groupIds.length === 0 || Database.isResetting()) {
      setGroupById({})
      return
    }

    let isMounted = true

    const fetchGroups = async () => {
      try {
        const groups = await Database.getCollection<GroupDoc>(
          CollectionType.GROUP
        )
          .query(Q.where('_id', Q.oneOf(groupIds)))
          .fetch()

        if (!isMounted || Database.isResetting()) return

        const nextGroupsById = groups.reduce(
          (acc, group) => {
            const id = group._id ?? ''
            if (!id) return acc
            acc[id] = group as Model<GroupDoc>
            return acc
          },
          {} as Record<string, Model<GroupDoc>>
        )

        setGroupById(nextGroupsById)
      } catch {
        if (!isMounted) return
        setGroupById({})
      }
    }

    fetchGroups()

    return () => {
      isMounted = false
    }
  }, [photosByGroup])

  const storageByGroupItems = useMemo(() => {
    const groupIds = Object.keys(photosByGroup)

    return groupIds
      .map((groupId) => {
        const group = groupById[groupId]
        const groupColor =
          props.userSettings?.GroupsSettings?.[groupId]?.GroupColor ??
          group?.GroupColor

        return {
          groupId,
          group,
          groupName: group?.Name,
          groupColor,
          photosUsed: photosByGroup[groupId] ?? 0
        } as StorageByGroupItem
      })
      .sort((a, b) => b.photosUsed - a.photosUsed)
  }, [groupById, photosByGroup, props.userSettings])

  const themeOptions = [
    { id: APP_THEME_MODES.SYSTEM, title: t('system') },
    { id: APP_THEME_MODES.LIGHT, title: t('light') },
    { id: APP_THEME_MODES.DARK, title: t('dark') }
  ]

  return (
    <PopupSection
      title={`${t('settings')}`}
      popupSectionGap={1}
      padding={'0.5rem 0rem 0rem 0rem'}
    >
      <PickLanguagePopup
        open={showLanguagePopup}
        setOpen={onShowLanguagePopup}
        selectItem={onLangChanged}
      >
        <span onClick={showLangauge}>
          <PopupSectionRow
            cursor="pointer"
            title={`${t('language')}`}
            iconClass="icon-regular"
            svgHeight="20px"
            maxWidthIcon={20}
            actionBg
            engulfEntireRow
            icon={<MenuLanguageIcon style={{ cursor: 'default' }} />}
            value={
              langs.find((lang) => i18n.language.includes(lang.lang))?.name
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </PickLanguagePopup>
      <SelectItemWithSearchPopup
        open={showFirstDayPopup}
        setOpen={onShowFirstDayPopup}
        selectItem={(item) => {
          const userSettings = Database.toServerObject<UserSettingsDoc>(
            props.userSettings
          ) as UserSettingsDoc
          userSettings.FirstDayOfTheWeek = Number(item.id ?? 1)
          updateUserSettings(userSettings).then(() => {
            appSettings.setFirstDayOfTheWeek(userSettings.FirstDayOfTheWeek)
            props.setMenuOpen(false)
          })
        }}
        data={days}
        selectedItemId={`${appSettings.firstDayOfTheWeek}`}
      >
        <span id="first-day-button" onClick={showFirstDay}>
          <PopupSectionRow
            title={`${t('firstDay')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<MenuFirstDayIcon style={{ cursor: 'default' }} />}
            value={
              days.find((day) => day.id === `${appSettings.firstDayOfTheWeek}`)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>
      {appSettings.isTimezoneFallback && <FallbackTimezoneSetting />}
      {props.account && (
        <DefaultCalendarSection {...props} account={props.account} />
      )}
      {isGroupcalApp() && (
        <>
          <span onClick={openLinkedCalendars}>
            <PopupSectionRow
              title={t('linkedCalendars')}
              iconClass="icon-regular"
              maxWidthIcon={20}
              cursor="pointer"
              actionBg
              engulfEntireRow
              icon={<MenuLinkedCalendarsIcon />}
            />
          </span>
        </>
      )}
      <SelectItemWithSearchPopup
        open={showThemePopup}
        setOpen={onShowThemePopup}
        selectItem={(item) => {
          onShowThemePopup(false)
          props.setMenuOpen(false)
          const mode = item.id as AppThemeMode

          if (
            mode === APP_THEME_MODES.SYSTEM ||
            mode === APP_THEME_MODES.LIGHT ||
            mode === APP_THEME_MODES.DARK
          ) {
            appSettings.setThemeMode(mode)
          }
        }}
        data={themeOptions}
        selectedItemId={appSettings.themeMode}
      >
        <span onClick={showThemeSelector}>
          <PopupSectionRow
            title={`${t('theme')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<DisplayModeIcon style={{ cursor: 'default' }} />}
            value={
              themeOptions.find((item) => item.id === appSettings.themeMode)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>

      {props.account && (
        <PopupSection
          title={t('storageUsed')}
          popupSectionGap={1}
          titlePadding={
            i18n.dir() === 'ltr'
              ? '0.8rem 0rem 0rem 0.1rem'
              : '0.8rem 0.1rem 0rem 0rem'
          }
          padding={'0.5rem 0rem 0rem 0rem'}
        >
          <StorageByGroupPopup
            open={showStoragePopup && isGroupcalApp()}
            setOpen={setShowStoragePopup}
            items={storageByGroupItems}
            totalPhotos={totalPhotos}
            photoQuotaLimit={photoQuotaLimit}
            accentColor={accentColor}
          >
            <span>
              <PopupSectionRow
                title={t('eventPhotoAttachments')}
                iconClass="icon-regular"
                maxWidthIcon={20}
                icon={<StorageLimitIcon style={{ cursor: 'default' }} />}
                action={
                  hasPhotosUsed && isGroupcalApp()
                    ? showStorageByGroup
                    : () => {}
                }
                actionBg
                engulfEntireRow={hasPhotosUsed}
                value={
                  <Box
                    sx={{
                      minWidth: '84px',
                      width: 'fit-content',
                      height: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 1,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      ml: storageValueMinGap,
                      mr: storageValueMinGapRtl
                    }}
                  >
                    {isStorageLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <StorageProgressLoader variant="counter" />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {storageValue}
                        </Box>
                        {hasPhotosUsed && isGroupcalApp() ? (
                          <ArrowAllCalendars
                            style={{
                              width: 22,
                              height: 22,
                              transform:
                                i18n.dir() === 'ltr' ? '' : 'rotate(180deg)',
                              cursor: 'pointer'
                            }}
                          />
                        ) : null}
                      </>
                    )}
                  </Box>
                }
                valueColor={
                  isNearQuota ? accentColor : theme.palette.text.disabled
                }
                cursor={hasPhotosUsed ? 'pointer' : 'default'}
              />
            </span>
          </StorageByGroupPopup>

          <Box
            sx={{
              mt: 0.1,
              pl: isLtr ? storageBarStart : storageBarEnd,
              pr: isLtr ? storageBarEnd : storageBarStart
            }}
          >
            {isStorageLoading ? (
              <Box
                sx={{
                  width: '100%',
                  height: '6px'
                }}
              >
                <StorageProgressLoader variant="bar" />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '999px',
                  backgroundColor: theme.palette.border.primary,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    width: `${usagePercent}%`,
                    height: '100%',
                    borderRadius: '999px',
                    backgroundColor: storageProgressColor,
                    transition: 'width 180ms ease'
                  }}
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              pt: 0.35,
              pl: isLtr ? 0 : storageBarEnd,
              pr: isLtr ? storageBarEnd : 0,
              minHeight: '20px'
            }}
          >
            <Box
              sx={{
                minWidth: '72px',
                width: 'fit-content',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              {!isStorageLoading && isNearQuota && recommendedPlan ? (
                <Box
                  onClick={() =>
                    showPaymentDialog(recommendedPlan, undefined, [
                      'EventPhotoAttachments'
                    ])
                  }
                  sx={{
                    color: accentColor,
                    py: '1px',
                    fontSize: '12px',
                    lineHeight: '14px',
                    fontWeight: 400,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      opacity: 0.85
                    }
                  }}
                >
                  {t('upgrade')}
                </Box>
              ) : null}
            </Box>
          </Box>
        </PopupSection>
      )}
    </PopupSection>
  )
}
